import { initializeApp, getApps, getApp } from 'firebase/app'
import { getAnalytics, isSupported, logEvent, setUserProperties } from 'firebase/analytics'
import { getClientEnv } from '@/assets/js/clientEnv'
import { getInstallChannel, INSTALL_CHANNEL_ANALYTICS_KEY } from '@/assets/js/installChannel'
import { getSecuritySignalsForAnalytics } from '@/assets/js/securitySignals'

/** Firebase Web 客户端配置（apiKey 可公开；生产环境建议在控制台限制域名与 API Key） */
const firebaseConfig = {
  apiKey: 'AIzaSyCrPBw022P9Tk2nPOblIBMHkggx5xig3YY',
  authDomain: 'appleshort-b410d.firebaseapp.com',
  projectId: 'appleshort-b410d',
  storageBucket: 'appleshort-b410d.firebasestorage.app',
  messagingSenderId: '247902772939',
  appId: '1:247902772939:web:34f1f94e369c8fb0e9054e',
  measurementId: 'G-E2ZDW4XCBN'
}

export function getFirebaseApp () {
  if (!getApps().length) {
    return initializeApp(firebaseConfig)
  }
  return getApp()
}

let analytics = null

const isProduction = process.env.NODE_ENV === 'production'
const isAnalyticsDebug = process.env.VUE_APP_FIREBASE_ANALYTICS_DEBUG === 'true'
const shouldReportAnalytics = isProduction && !isAnalyticsDebug

function parseIsNotOrganicValue (value) {
  return value === true || value === 1 || value === '1' || value === 'true'
}

function parseIsVipValue (value) {
  return value === true || value === 1 || value === '1' || value === 'true'
}

/** 读取 /api/user/detail 返回并持久化的 isNotOrganic（data 根字段） */
function getIsNotOrganic () {
  try {
    const raw = JSON.parse(localStorage.getItem('user_info') || 'null')
    if (!raw || typeof raw !== 'object') return false
    return parseIsNotOrganicValue(raw.isNotOrganic ?? raw.is_not_organic)
  } catch {
    return false
  }
}

/** 读取 /api/user/detail 返回并持久化的 vip 状态 */
function getIsVip () {
  try {
    const raw = JSON.parse(localStorage.getItem('user_info') || 'null')
    if (!raw || typeof raw !== 'object') return false
    return parseIsVipValue(raw.vip)
  } catch {
    return false
  }
}

/** 通用参数：runtime / device_type / install_channel / isNotOrganic / is_vip / 地区信号（事件参数与用户属性共用） */
export function getAnalyticsEnvParams () {
  const env = getClientEnv()
  const signals = getSecuritySignalsForAnalytics()
  const params = {
    runtime: env.runtime || 'browser',
    device_type: env.deviceType || 'unknown',
    device_region: signals.device_region,
    device_language: signals.device_language,
    device_timezone: signals.device_timezone,
    isNotOrganic: getIsNotOrganic() ? 'true' : 'false',
    is_vip: getIsVip() ? 'true' : 'false'
  }
  const installChannel = getInstallChannel()
  if (installChannel) {
    params[INSTALL_CHANNEL_ANALYTICS_KEY] = installChannel
  }
  return params
}

function applyAnalyticsUserProperties () {
  if (!analytics) return
  setUserProperties(analytics, {
    ...getAnalyticsEnvParams(),
    ...getSecuritySignalsForAnalytics()
  })
}

export function refreshAnalyticsUserProperties () {
  applyAnalyticsUserProperties()
}

/**
 * 在浏览器中初始化 Analytics（不支持的环境会静默跳过）
 * @returns {Promise<import('firebase/analytics').Analytics | null>}
 */
export async function initFirebaseAnalytics () {
  if (typeof window === 'undefined') return null
  if (!shouldReportAnalytics) return null
  try {
    const supported = await isSupported()
    if (!supported) return null
    analytics = getAnalytics(getFirebaseApp())
    applyAnalyticsUserProperties()
    return analytics
  } catch {
    return null
  }
}

/**
 * 上报 GA4 事件：正式环境实际上报，debug/开发环境仅输出日志
 * @param {string} name
 * @param {Record<string, unknown>} [params]
 */
export function logAnalyticsEvent (name, params = {}) {
  const payload = {
    ...params,
    ...getAnalyticsEnvParams()
  }

  if (shouldReportAnalytics) {
    if (!analytics) return
    logEvent(analytics, name, payload)
    return
  }
  logger.debug('[Firebase Analytics]', name, payload)
}

export function getFirebaseAnalytics () {
  return analytics
}
